import { Box, Dialog } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { ResponsiveStyleValue } from "@mui/system";
import { motion } from "framer-motion";
import React, { type CSSProperties, type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthProvider";
import { localStorageService } from "@/services/LocalStorageService";
import { INK_BLACK, SURFACE } from "@/theme/tokens";
import { trackEvent } from "@/utils/analytics";
import { MultiSizePopupHeader } from "./MultiSizePopupHeader";
import { MultiSizePopupState, minimizedViewMaxHeight, minimizedViewMaximumContentWidth, type PreviousStateType } from "./utils";

/**
 * Who owns the popup's size. Uncontrolled (neither prop): the popup keeps its own size, seeded from
 * `popupLocalStorageKey` or MAXIMIZED, exactly as it always has. Controlled (both): the caller holds the
 * size — so it can bring a HIDDEN popup back from outside, e.g. when an entry point is used again — and
 * every header action asks for a new one through `onSizeStateChange`. The two go together, so a caller
 * can't set a size it never hears change.
 */
type SizeControlProps =
	| { sizeState?: undefined; onSizeStateChange?: undefined }
	| {
			sizeState: MultiSizePopupState;
			/** Called with the size a header action asks for; the popup shows it once the caller passes it back. */
			onSizeStateChange: (next: MultiSizePopupState) => void;
	  };

/**
 * iOS Safari's `100vh` is the height with its toolbar retracted, so a box that tall runs under the toolbar and its
 * bottom (a sticky footer, the last rows) can't be reached. Where the dynamic unit exists it is the visible height;
 * elsewhere the `vh` value stays. On a desktop browser the two are the same, so no consumer changes there.
 */
const DYNAMIC_VIEWPORT_SUPPORTED = "@supports (height: 100dvh)";

type Props = SizeControlProps & {
	title: ReactNode;
	open: boolean;
	bodyRenderer: (currentSizeState: MultiSizePopupState) => ReactNode;
	maxContentWidth?: CSSProperties["maxWidth"];
	/**
	 * The frame's width while maximized: a CSS width, where a percentage is of the window the popup floats in, or a
	 * breakpoint object. Absent, the frame fits its content (up to `maxContentWidth`), as it always has.
	 *
	 * Give one when the body should fill the popup, like a form. A frame that fits its content is as wide as the body
	 * plus the vertical scrollbar the browser counted when it last measured widths. When the scrollbar appears without
	 * a new measurement (the window gets shorter), the body is a scrollbar narrower than its content and scrolls
	 * sideways. With a set width, the body just fills what the scrollbar leaves.
	 */
	maximizedWidth?: ResponsiveStyleValue<string>;
	/**
	 * Minimized-view max height, in PIXELS.
	 *
	 * Numeric rather than a CSS length: the scroll body below builds `calc(${value}px - 50px)`, so a
	 * string like "50vh" produced `calc(50vhpx - 50px)` — invalid, and it silently killed the body's
	 * max-height and its scrolling.
	 */
	minimizedMaxContentHeight?: number;
	onClose?: (e: React.MouseEvent<HTMLElement>) => void;
	popupLocalStorageKey?: string;
	disableMaximize?: boolean;
	disableMinimize?: boolean;
	closeOnEscape?: boolean;
	/** Close when the maximized popup's surrounding area is clicked; the paper covers the backdrop, so the Dialog never sees it. */
	closeOnClickOutside?: boolean;
	disableHide?: boolean;
	/**
	 * Show the header's hide, resize and close buttons, but disabled — e.g. while the body is saving, so the admin
	 * can't put aside or close what they're waiting on. Unlike the `disable*` props it keeps them in place, so the
	 * header doesn't lose and regain buttons around a short action. Escape still goes to `onClose`, which decides.
	 */
	headerButtonsDisabled?: boolean;
	"data-testid"?: string;
	/**
	 * If true, the body content will remain mounted in the DOM even when the popup is minimized or hidden.
	 */
	keepBodyMounted?: boolean;
	/** Extra controls (e.g. a reset button) rendered in the header next to the built-in hide/resize/close icons. Only visible when maximized. */
	headerActions?: ReactNode;
};

export const MultiSizePopup = ({
	title,
	open,
	bodyRenderer,
	maxContentWidth,
	maximizedWidth,
	minimizedMaxContentHeight,
	onClose,
	popupLocalStorageKey,
	disableMinimize = false,
	disableMaximize = false,
	disableHide = false,
	headerButtonsDisabled = false,
	closeOnEscape = false,
	closeOnClickOutside = false,
	"data-testid": dataTestId,
	keepBodyMounted = false,
	headerActions,
	sizeState: controlledSizeState,
	onSizeStateChange,
}: Props) => {
	const {
		userInfo: { id: userId },
	} = useAuth();
	const initialState = useMemo(
		() =>
			popupLocalStorageKey
				? localStorageService.getMultiSizePopupState(popupLocalStorageKey, userId)
				: disableMaximize
					? MultiSizePopupState.MINIMIZED
					: MultiSizePopupState.MAXIMIZED,
		[popupLocalStorageKey, disableMaximize, userId],
	);
	const [internalSizeState, setInternalSizeState] = useState<MultiSizePopupState>(initialState);
	const sizeState = controlledSizeState === undefined ? internalSizeState : controlledSizeState;
	const [previousSizeState, setPreviousSizeState] = useState<PreviousStateType>(
		initialState !== MultiSizePopupState.HIDDEN ? initialState : MultiSizePopupState.MAXIMIZED,
	);
	// The last size that wasn't HIDDEN, which "un-hide" returns to. A controlled caller can set a size
	// directly (bringing the popup back), so it's followed here from whatever is shown — React's pattern for
	// state that tracks a prop. Uncontrolled, the header's own change already stored it, so this is a no-op.
	if (sizeState !== MultiSizePopupState.HIDDEN && sizeState !== previousSizeState) {
		setPreviousSizeState(sizeState);
	}
	const isMax = sizeState === MultiSizePopupState.MAXIMIZED;
	const hasMaximizedWidth = isMax && maximizedWidth !== undefined;
	const theme = useTheme();
	// A drag that starts in the paper and ends outside clicks their common ancestor (the root), so only a press that
	// also began on the root counts as a click outside.
	const pressedOutside = useRef(false);
	const titleId = useId();
	const contentRef = useRef<HTMLDivElement>(null);

	const handleSizeStateChange = useCallback(
		(e: React.MouseEvent<HTMLElement>, newSizeState: MultiSizePopupState) => {
			e.stopPropagation();
			if (newSizeState !== MultiSizePopupState.HIDDEN) {
				setPreviousSizeState(newSizeState);
				if (popupLocalStorageKey) {
					localStorageService.setMultiSizePopupState(popupLocalStorageKey, newSizeState, userId);
				}
			}
			if (onSizeStateChange) {
				onSizeStateChange(newSizeState);
			} else {
				setInternalSizeState(newSizeState);
			}
			trackEvent("multi_size_popup_size_change", {
				popupKey: popupLocalStorageKey,
				newSizeState,
			});
		},
		[popupLocalStorageKey, userId, onSizeStateChange],
	);

	// Brought back to MAXIMIZED from outside (a controlled caller restoring a hidden popup): the focus is still
	// on the page control that asked, so move it in, as opening would have. A restore from the popup's own
	// header already has focus inside, and is left alone.
	const wasMax = useRef(isMax);
	useEffect(() => {
		const becameMax = isMax && !wasMax.current;
		wasMax.current = isMax;
		const content = contentRef.current;
		if (!open || !becameMax || !content || content.contains(document.activeElement)) {
			return;
		}
		// The same move MUI's focus trap makes on open: a -1 tab index so the surface itself can take focus.
		if (!content.hasAttribute("tabindex")) {
			content.setAttribute("tabindex", "-1");
		}
		content.focus();
	}, [isMax, open]);

	useEffect(() => {
		if (!open) {
			return;
		}
		trackEvent("multi_size_popup_opened", {
			popupKey: popupLocalStorageKey,
			initialState,
		});
	}, [open, popupLocalStorageKey, initialState]);

	return (
		<Dialog
			data-testid={dataTestId}
			aria-labelledby={titleId}
			open={open}
			onClose={(event, reason) => {
				if (closeOnEscape && reason === "escapeKeyDown" && isMax) {
					onClose?.(event as React.MouseEvent<HTMLElement>);
				}
			}}
			hideBackdrop={!isMax}
			disableScrollLock={!isMax}
			// Hidden, the popup is just its header bar, set aside at the corner so the user can work on the page: it
			// neither takes nor holds focus, or Tab and clicks on the page would be pulled back into it. Minimized and
			// maximized keep the dialog's focus trap and its focus on open, as they always have.
			disableEnforceFocus={sizeState === MultiSizePopupState.HIDDEN}
			disableAutoFocus={sizeState === MultiSizePopupState.HIDDEN}
			slotProps={{
				root: {
					sx: {
						pointerEvents: isMax ? "auto" : "none",
					},
				},
				container: {
					sx: {
						display: "flex",
						width: "100vw",
						height: "100vh",
						maxHeight: "100vh",
						[DYNAMIC_VIEWPORT_SUPPORTED]: { height: "100dvh", maxHeight: "100dvh" },
						alignItems: "center",
						justifyContent: "center",
						pointerEvents: isMax ? "auto" : "none",
					},
				},
				paper: {
					sx: {
						p: 0,
						m: 0,
						display: "flex",
						width: "100%",
						height: "100%",
						alignItems: "center",
						justifyContent: "center",
						borderRadius: 0,
						background: alpha(INK_BLACK, 0),
						boxShadow: "none",
						maxWidth: "100%",
						maxHeight: "100%",
						overflow: "clip",
						pointerEvents: isMax ? "auto" : "none",
					},
				},
			}}
		>
			<Box
				id={"multi-size-root"}
				key={"multi-size-root"}
				onMouseDown={(e: React.MouseEvent<HTMLElement>) => {
					pressedOutside.current = e.target === e.currentTarget;
				}}
				onClick={(e: React.MouseEvent<HTMLElement>) => {
					const outside = pressedOutside.current && e.target === e.currentTarget;
					pressedOutside.current = false;
					if (closeOnClickOutside && isMax && outside) onClose?.(e);
				}}
				sx={{
					display: "flex",
					alignItems: isMax ? "center" : "end",
					justifyContent: isMax ? "center" : "end",
					height: "100%",
					width: "100%",
					pointerEvents: isMax ? "auto" : "none",
				}}
			>
				<Box
					ref={contentRef}
					component={motion.div}
					layout={"position"}
					transition={{
						layout: {
							type: "spring",
							damping: 30,
							stiffness: 400,
						},
					}}
					// A set maximized width goes through `sx` so it can vary by breakpoint; the inline `style` would outrank it.
					sx={hasMaximizedWidth ? { width: maximizedWidth } : undefined}
					style={{
						background: SURFACE,
						// Never wider than the window: a fixed 450px floor put the popup's edges off a phone's screen.
						minWidth: `min(${minimizedViewMaximumContentWidth}px, 100vw)`,
						width: hasMaximizedWidth ? undefined : "fit-content",
						maxWidth: isMax ? maxContentWidth : `min(${minimizedViewMaximumContentWidth}px, 100vw)`,
						maxHeight: !isMax ? (minimizedMaxContentHeight ?? minimizedViewMaxHeight) : "100%",
						height: "fit-content",
						overflowY: "clip",
						borderRadius: isMax ? "8px" : "8px 8px 0px 0px",
						border: 1,
						overflow: "clip",
						boxShadow: `0px 0px 6px 3px ${alpha(theme.palette.primary.main, 0.15)}`,
						pointerEvents: "auto",
					}}
				>
					<Box component={motion.div} layout={false}>
						<MultiSizePopupHeader
							previousState={previousSizeState}
							sizeState={sizeState}
							onClose={(e) => onClose?.(e)}
							title={title}
							onStateChange={handleSizeStateChange}
							disableMinimize={disableMinimize}
							disableMaximize={disableMaximize}
							disableHide={disableHide}
							buttonsDisabled={headerButtonsDisabled}
							headerActions={headerActions}
							titleId={titleId}
							dataTestId={dataTestId}
						/>
					</Box>
					<Box
						component={motion.div}
						layout={false}
						sx={{
							width: "100%",
							height: "fit-content",
							maxHeight: `calc(${!isMax ? `${minimizedMaxContentHeight ?? minimizedViewMaxHeight}px` : "100vh"} - 50px)`,
							...(isMax && { [DYNAMIC_VIEWPORT_SUPPORTED]: { maxHeight: "calc(100dvh - 50px)" } }),
							overflowY: "auto",
						}}
					>
						{(keepBodyMounted || sizeState !== MultiSizePopupState.HIDDEN) && bodyRenderer(sizeState)}
					</Box>
				</Box>
			</Box>
		</Dialog>
	);
};
